"use client";

import { useEffect, useRef, type Ref } from "react";
import { useInView } from "@/lib/use-in-view";

type WigglyArrowProps = {
  /** Stroke colour — defaults to currentColor */
  color?: string;
  /** Stroke width — defaults to 3 */
  strokeWidth?: number;
  /** Body draw duration in ms — defaults to 1200 */
  duration?: number;
  /** Body easing — defaults to cubic-bezier(0.4,0,0.2,1) */
  easing?: string;
  /** Width of the rendered SVG — defaults to 95 */
  width?: number;
  /** Height of the rendered SVG — defaults to 45 */
  height?: number;
  /** Delay before animation starts in ms — defaults to 0 */
  delay?: number;
  className?: string;
};

export const WigglyArrow = ({
  color = "currentColor",
  strokeWidth = 3,
  duration = 1200,
  easing = "cubic-bezier(0.4,0,0.2,1)",
  width = 95,
  height = 45,
  delay = 0,
  className,
}: WigglyArrowProps) => {
  const bodyRef = useRef<SVGPathElement>(null);
  const headLeftRef = useRef<SVGPathElement>(null);
  const headRightRef = useRef<SVGPathElement>(null);
  const hasAnimated = useRef(false);
  const { ref: inViewRef, inView } = useInView();

  useEffect(() => {
    if (!inView || hasAnimated.current) return;
    hasAnimated.current = true;

    const body = bodyRef.current;
    const headLeft = headLeftRef.current;
    const headRight = headRightRef.current;
    if (!body || !headLeft || !headRight) return;

    const bodyLen = body.getTotalLength();
    const leftLen = headLeft.getTotalLength();
    const rightLen = headRight.getTotalLength();
    const headDuration = Math.round(duration * 0.2);

    // Reset all paths
    const reset = (el: SVGPathElement, len: number) => {
      el.style.transition = "none";
      el.style.strokeDasharray = String(len);
      el.style.strokeDashoffset = String(len);
    };

    reset(body, bodyLen);
    reset(headLeft, leftLen);
    reset(headRight, rightLen);

    const drawHeads = () => {
      const t = `stroke-dashoffset ${headDuration}ms ease-out`;
      headLeft.style.transition = t;
      headRight.style.transition = t;
      headLeft.style.strokeDashoffset = "0";
      headRight.style.strokeDashoffset = "0";
    };

    const timer = window.setTimeout(() => {
      body.style.transition = `stroke-dashoffset ${duration}ms ${easing}`;
      body.style.strokeDashoffset = "0";
      body.addEventListener("transitionend", drawHeads, { once: true });
    }, delay);

    return () => {
      window.clearTimeout(timer);
      body.removeEventListener("transitionend", drawHeads);
    };
  }, [inView, duration, easing, delay]);

  const sharedProps = {
    stroke: color,
    strokeWidth,
    strokeLinecap: "round" as const,
    fill: "none",
  };

  return (
    <svg
      ref={inViewRef as Ref<SVGSVGElement>}
      width={width}
      height={height}
      viewBox="0 0 95 45"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        ref={bodyRef}
        d="M93 40.9269C79.5 33.4269 62.8491 31.9058 39.3942 40.9269C10.763 51.9389 6.0894 19.7126 7.32986 2.22302"
        {...sharedProps}
      />
      <path
        ref={headLeftRef}
        d="M7.32986 2.22302L1.49999 8.8626"
        {...sharedProps}
      />
      <path
        ref={headRightRef}
        d="M7.32986 2.22302L13.6456 8.8626"
        {...sharedProps}
      />
    </svg>
  );
};
